/**
 * Video History Component - Glass Morphism Design
 * Displays list of video generation jobs
 * Validates: Requirements 17.6
 */
import { VideoJob } from '../../stores/videoStore';
import { VideoJobCard } from './VideoJobCard';
import { Icons } from '../icons';
import { THEME } from '../../styles/theme';

interface VideoHistoryProps {
  jobs: VideoJob[];
  currentJobId: string | null;
  isLoading: boolean;
  onSelectJob: (jobId: string) => void;
}

export function VideoHistory({
  jobs,
  currentJobId,
  isLoading,
  onSelectJob,
}: VideoHistoryProps) {
  if (isLoading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="rounded-lg h-48"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="p-8 text-center">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
          style={{
            backgroundColor: 'rgba(255,255,255,0.03)',
            border: `1px solid ${THEME.border}`,
          }}
        >
          <Icons.Video size={32} style={{ color: THEME.textMuted }} />
        </div>
        <h3 className="text-lg font-medium mb-2" style={{ color: THEME.textMain }}>
          No videos yet
        </h3>
        <p className="text-sm" style={{ color: THEME.textSecondary }}>
          Generate your first video using the form above.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <h3
        className="text-sm font-semibold uppercase tracking-wider"
        style={{ color: THEME.textMuted }}
      >
        Generation History
      </h3>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {jobs.map((job) => (
          <VideoJobCard
            key={job.id}
            job={job}
            isSelected={job.id === currentJobId}
            onClick={() => onSelectJob(job.id)}
          />
        ))}
      </div>
    </div>
  );
}
